import fetcher from '../fetcher';
import { ICalendar, IDateInterval, ICalendarIntervals, ICalendarIntervalRequest, CalendarType, ICalendarArriveTimeRequest, ICalendarAllIntervalRequest, IDateIntervalByCalendar } from './../../entities/calendar';
import { IDTO, IResponseDto } from './../../entities/common';
import { handleError } from './errorHandler';

export const getCalendars = (): Promise<ICalendar[]> => {
    return fetcher.get<IDTO<ICalendar[]>>('/Calendars/WithDetails')
        .then(respJson => respJson.Data || [])
};

export const getCalendarById = (calendarId?: number): Promise<ICalendar> => {
    return fetcher.get<IDTO<ICalendar>>(`/Calendars/${calendarId}`)
        .then((respJson) => respJson.Data);
};

export const getCalendarsByService = (id: number): Promise<ICalendar[]> => {
    return fetcher.get<IDTO<ICalendar[]>>(`/Services/${id}/Calendars/WithDetails`)
        .then(respJson => respJson.Data.filter(calendar => calendar.Type !== CalendarType.Timetable) || [], () => []);
};

export const getCalendarFirstFreeSlot = (calendarId: number, serviceId?: number): Promise<string> => {
    const params = serviceId ?
        '?' + new URLSearchParams({
            ServiceId: '' + serviceId,
        }).toString() : ''
    return fetcher.get<IDTO<string>>(`/Calendars/${calendarId}/FirstFreeSlot${params}`)
        .then((respJson) => respJson.Data || '')
        .catch(() => '');
};

export const getCalendarDaysWithFreeIntervals =
    (request: ICalendarIntervalRequest): Promise<string[] | null> => {
        return fetcher.post<IDTO<string[]>>(`/Calendars/${request.calendarId}/DaysWithFreeIntervals`, request)
            .then(response => {
                return response.Data
            }, (errorDto: IResponseDto) => {
                return Promise.reject(handleError(errorDto));
            });
    };

/**
 * Fetch free appointment time intervals on a given day or in a set of days
 * @property startDate - Single day or first in a set of days
 * @property [endDate] - Last in a set days
 */
export const getCalendarFreeIntervals =
    (request: ICalendarIntervalRequest): Promise<IDateInterval[] | null> => {
        request.endDate = request.endDate || request.startDate

        return fetcher.post<IDTO<ICalendarIntervals>>(`/Calendars/${request.calendarId}/FreeIntervals`, request)
            .then(response => {
                return response.Data.Intervals
            }, (errorDto: IResponseDto) => {
                return Promise.reject(handleError(errorDto));
            });
    }

export const getCalendarAllIntervals =
    (request: ICalendarAllIntervalRequest): Promise<IDateIntervalByCalendar[] | null> => {
        request.endDate = request.endDate || request.startDate;
        const calendarIdsParam = new URLSearchParams(
            request.calendarIds.map(s => ['calendarIds', s]));

        const params = new URLSearchParams({
            StartDate: request.startDate,
            EndDate: request.endDate,
            UseSlotMinutes: false.toString()
        });

        const queryString = '?' + params.toString() + '&' + calendarIdsParam.toString();

        return fetcher.get<IDTO<IDateIntervalByCalendar[]>>(`/Calendars/AllIntervals${queryString}`)
            .then(response => {
                return response.Data
            }, (errorDto: IResponseDto) => {
                return Promise.reject(handleError(errorDto));
            });
    }

export const getCalendarArriveTimes =
    (request: ICalendarArriveTimeRequest): Promise<string[]> => {
        const params = 
            '?' + new URLSearchParams({
                ServiceId: '' + request.serviceId,
                Date: request.startDate
            }).toString()

        return fetcher.get<IDTO<string[]>>(`/Calendars/${request.calendarId}/ArrivalTimes${params}`)
            .then(response => {
                return response.Data
            }, (errorDto: IResponseDto) => {
                return Promise.reject(handleError(errorDto));
            });
    }

export const googleSyncByCalendar = (calendarId: number): Promise<IResponseDto> => {
    return fetcher.post<IResponseDto>(`/Calendars/${calendarId}/GoogleSync`, {});
}

export const googleSyncAll = (): Promise<IResponseDto> => {
    return fetcher.post<IResponseDto>(`/Calendars/GoogleSync`, {});
}